import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';
import { Mail, Phone, ArrowLeft, RefreshCw } from 'lucide-react';
import { useOTP } from '@hooks/useOTP';
import { ROUTES, OTP_TYPES, STORAGE_KEYS } from '@constants';
import Button from '@components/common/Button';
import Input from '@components/common/Input';
import Card from '@components/common/Card';
import { localStorage } from '@utils/storage';

const otpSchema = z.object({
  emailOtp: z.string().length(6, 'OTP must be 6 digits'),
  phoneOtp: z.string().length(6, 'OTP must be 6 digits'),
});

const VerifyOTPPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { verifyEmailOTP, verifyPhoneOTP, resendOTP, loading } = useOTP();
  
  const [pendingRegistrationId, setPendingRegistrationId] = useState(null);
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [emailVerified, setEmailVerified] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(otpSchema),
  });

  useEffect(() => {
    // Get pending registration data from location state or localStorage
    const stateData = location.state;
    const storedPendingId = localStorage.get(STORAGE_KEYS.PENDING_USER_ID);
    
    if (stateData?.pendingRegistrationId) {
      setPendingRegistrationId(stateData.pendingRegistrationId);
      setEmail(stateData.email || '');
      setPhone(stateData.phone || '');
      localStorage.set(STORAGE_KEYS.PENDING_USER_ID, stateData.pendingRegistrationId);
    } else if (storedPendingId) {
      setPendingRegistrationId(storedPendingId);
    } else {
      toast.error('No pending verification found');
      navigate(ROUTES.REGISTER);
    }
  }, [location, navigate]);

  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleVerifyEmail = async (data) => {
    try {
      const result = await verifyEmailOTP(pendingRegistrationId, data.emailOtp);
      if (result.success) {
        setEmailVerified(true);
        toast.success('Email verified successfully!');
        
        // Check if user was created (both verifications complete)
        if (result.data?.userCreated) {
          handleBothVerified();
        } else if (phoneVerified) {
          handleBothVerified();
        }
      }
    } catch (error) {
      toast.error(error.message || 'Failed to verify email OTP');
    }
  };

  const handleVerifyPhone = async (data) => {
    try {
      const result = await verifyPhoneOTP(pendingRegistrationId, data.phoneOtp);
      if (result.success) {
        setPhoneVerified(true);
        toast.success('Phone verified successfully!');
        
        // Check if user was created (both verifications complete)
        if (result.data?.userCreated) {
          handleBothVerified();
        } else if (emailVerified) {
          handleBothVerified();
        }
      }
    } catch (error) {
      toast.error(error.message || 'Failed to verify phone OTP');
    }
  };

  const handleBothVerified = () => {
    toast.success('Both verifications complete! Your account has been created. You can now login.');
    localStorage.remove(STORAGE_KEYS.PENDING_USER_ID);
    setTimeout(() => {
      navigate(ROUTES.LOGIN);
    }, 1500);
  };

  const handleResendOTP = async (type) => {
    if (resendCooldown > 0) {
      toast.error(`Please wait ${resendCooldown} seconds before resending`);
      return;
    }

    try {
      const result = await resendOTP(pendingRegistrationId, type);
      if (result.success) {
        toast.success(result.message || 'OTP sent successfully');
        setResendCooldown(60); // 60 seconds cooldown
      }
    } catch (error) {
      toast.error(error.message || 'Failed to resend OTP');
    }
  };

  const onSubmit = async (data) => {
    if (!emailVerified) {
      await handleVerifyEmail(data);
    }
    if (!phoneVerified && emailVerified) {
      await handleVerifyPhone(data);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-100 to-primary-200 flex items-center justify-center p-4">
      <div className="w-full">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-primary-600 mb-2">
            Verify Your Account
          </h1>
          <p className="text-primary-700">
            Enter the OTP sent to your email and phone
          </p>
        </div>

        <Card className="p-8">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Email OTP */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Mail className="w-5 h-5 text-primary-600" />
                  <h3 className="font-semibold text-primary-700">
                    Email Verification
                  </h3>
                </div>
                {emailVerified && (
                  <span className="text-sm text-secondary-600 font-medium">
                    ✓ Verified
                  </span>
                )}
              </div>

              {email && (
                <p className="text-sm text-primary-600 mb-2">
                  OTP sent to: {email}
                </p>
              )}

              <Input
                label="Email OTP"
                type="text"
                placeholder="Enter 6-digit OTP"
                maxLength={6}
                disabled={emailVerified}
                error={errors.emailOtp?.message}
                fullWidth
                {...register('emailOtp')}
              />

              {!emailVerified && (
                <button
                  type="button"
                  onClick={() => handleResendOTP(OTP_TYPES.EMAIL)}
                  disabled={resendCooldown > 0 || loading}
                  className="mt-2 text-sm text-secondary-500 hover:underline disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                >
                  <RefreshCw className="w-4 h-4" />
                  {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend OTP'}
                </button>
              )}
            </div>

            {/* Phone OTP */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Phone className="w-5 h-5 text-primary-600" />
                  <h3 className="font-semibold text-primary-700">
                    Phone Verification
                  </h3>
                </div>
                {phoneVerified && (
                  <span className="text-sm text-secondary-600 font-medium">
                    ✓ Verified
                  </span>
                )}
              </div>

              {phone && (
                <p className="text-sm text-primary-600 mb-2">
                  OTP sent to: {phone}
                </p>
              )}

              <Input
                label="Phone OTP"
                type="text"
                placeholder="Enter 6-digit OTP"
                maxLength={6}
                disabled={phoneVerified}
                error={errors.phoneOtp?.message}
                fullWidth
                {...register('phoneOtp')}
              />

              {!phoneVerified && (
                <button
                  type="button"
                  onClick={() => handleResendOTP(OTP_TYPES.PHONE)}
                  disabled={resendCooldown > 0 || loading}
                  className="mt-2 text-sm text-secondary-500 hover:underline disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                >
                  <RefreshCw className="w-4 h-4" />
                  {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend OTP'}
                </button>
              )}
            </div>

            <Button
              type="submit"
              variant="primary"
              fullWidth
              loading={loading}
              disabled={emailVerified && phoneVerified}
            >
              {emailVerified && phoneVerified
                ? 'Verification Complete'
                : !emailVerified
                ? 'Verify Email'
                : 'Verify Phone'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => navigate(ROUTES.LOGIN)}
              className="inline-flex items-center gap-2 text-sm text-secondary-500 hover:underline"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Login
            </button>
          </div>
        </Card>

        <div className="text-center mt-6 text-sm text-primary-600">
          <p>Both email and phone must be verified to login</p>
        </div>
      </div>
    </div>
  );
};

export default VerifyOTPPage;
